import { Alert } from '@catering/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { JSX } from 'react';

import { useCurrentUser } from '../auth/use-current-user';
import { usePortalMe } from '../shell/use-portal-me';
import { OrderForm } from './order-form';
import { hasOrderPermission } from './order-permissions';
import { useOrder } from './use-order';

/**
 * Wraps `OrderForm` with routing: `/orders/new` (create) and
 * `/orders/$orderId/edit` (edit, pre-cutoff). Sites come from
 * `GET /portal/me`, already fetched by `AppShell`/`ProtectedRoute` —
 * `usePortalMe` shares that cache instead of a second request.
 */
export function OrderFormPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const orderId = params.orderId;
  const me = usePortalMe(true);
  const existingOrder = useOrder(orderId);
  const currentUser = useCurrentUser();

  if (me.isPending || (orderId !== undefined && existingOrder.isPending) || currentUser.isPending) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  if (me.isError || me.data === undefined || me.data.status !== 'ACTIVE') {
    return <Alert tone="danger">No pudimos cargar tus sedes. Probá de nuevo más tarde.</Alert>;
  }

  const roleCodes = currentUser.data?.roleCodes ?? [];
  const requiredPermission = orderId === undefined ? 'order.create' : 'order.update';

  if (!hasOrderPermission(roleCodes, requiredPermission)) {
    return (
      <Alert tone="danger">
        Tu acceso no permite {orderId === undefined ? 'cargar' : 'editar'} pedidos.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold text-slate-900">
        {orderId === undefined ? 'Cargar pedido' : 'Editar pedido'}
      </h1>

      <OrderForm
        sites={me.data.sites}
        operatingUnits={me.data.operatingUnits}
        order={existingOrder.data}
        onSubmitted={() => void navigate({ to: '/orders' })}
      />
    </div>
  );
}
